/**
 * AB实验配置数据转换工具函数
 */
import React from 'react';
import { format } from 'sql-formatter';
import { Button, Modal, notification } from 'tea-component';
import AceEditor from 'react-ace';
import { checkNullString, copyTextToClipboard } from '@/pages/datalab/utils/utils';
import { IColumnInfo } from '@dbl/core/dataSource';
import { convertColumnType } from '@dbl/core/dataSource';
import { FilterMethod } from '@dbl/core/filter';
import {
  IAbConfigFilter,
  IAbConfigMetric,
  IAbConfigDimension,
  IAbConfigEventFilter,
  IGroupableItem,
  IOptionMenu,
  IAbConfigExperimentFilter,
  IEventListItem,
  ICascaderOption,
  TFormatMessage,
} from './types';

/**
 * 展示 SQL 预览弹窗：校验 SQL、格式化后以 Modal + AceEditor 展示，支持复制
 */
export function showPreviewSqlModal(params: {
  sql: string;
  formatMessage: TFormatMessage;
}): void {
  const { sql, formatMessage } = params;
  if (checkNullString(sql)) {
    notification.error({
      description: formatMessage({ id: 'dbl_ab_experiment_preview_sql_fail' }),
    });
    return;
  }
  let formatSql: string;
  try {
    formatSql = format(sql, { language: 'mysql' });
  } catch (e) {
    console.error('预览 SQL 解析失败', { e, sql });
    formatSql = sql;
  }
  const modal = Modal.show({
    className: 'preview-page',
    maskClosable: true,
    caption: React.createElement(
      React.Fragment,
      null,
      React.createElement(
        'span',
        { style: { verticalAlign: 'middle' } },
        formatMessage({ id: 'dbl_ab_experiment_preview_sql' })
      ),
      React.createElement(
        Button,
        {
          type: 'link',
          style: { fontSize: '20px', marginRight: '5px' },
          onClick: () => {
            copyTextToClipboard(formatSql)?.then(
              () => {
                notification.success({
                  description: formatMessage({ id: 'dbl_common_copy_success' }),
                });
              },
              (err: unknown) => {
                console.error('SQL 复制错误: ', err);
                notification.error({
                  description: formatMessage({ id: 'dbl_common_copy_error' }),
                });
              }
            );
          },
        },
        ` (${formatMessage({ id: 'dbl_ab_experiment_preview_sql_copy' })})`
      )
    ),
    size: 'l',
    children: React.createElement(
      Modal.Body,
      { className: 'preview-body' },
      React.createElement(
        'div',
        { className: 'preview-model' },
        React.createElement(AceEditor, {
          height: '700px',
          mode: 'mysql',
          theme: 'tomorrow',
          fontSize: 13,
          width: '100%',
          showPrintMargin: false,
          showGutter: true,
          highlightActiveLine: false,
          editorProps: { $blockScrolling: true },
          value: formatSql,
        })
      )
    ),
    onClose: () => modal.destroy(),
  });
}

export { convertColumnType };

/**
 * 根据字段类型确定 filter_method
 */
function getFilterMethodByColumnType(type: string): number {
  if (type === 'date') {
    return FilterMethod.Date;
  }
  if (type === 'groupCascade') {
    return FilterMethod.GroupCascade;
  }
  if (type === 'string') {
    return FilterMethod.String;
  }
  // 默认使用 NONE
  return FilterMethod.None;
}

/**
 * 判断配置项是否适用于当前用户类型
 * @param applicableUserTypes - 适用用户类型列表，不传或空或含 'all' 时对所有用户类型生效
 * @param userType - 当前用户类型
 * @returns 是否适用于当前用户类型
 */
export function isApplicableByUserType(
  applicableUserTypes: string[] | undefined,
  userType: string | undefined
): boolean {
  if (!userType) return true;
  if (!applicableUserTypes?.length || applicableUserTypes.includes('all')) return true;
  return applicableUserTypes.includes(userType);
}

/**
 * 将筛选器数据转换为 IColumnInfo
 */
export function convertFilterToColumnInfo(filter: IAbConfigFilter, index: number): IColumnInfo {
  return {
    id: `filter_${filter.mapping_id}_${index}`,
    table_id: String(filter?.mapping_id ?? ''),
    table_code: '',
    column_name: filter?.display_name_en ?? '',
    column_display: filter?.display_name_cn ?? '',
    column_display_en: filter?.display_name_en ?? '',
    column_type: convertColumnType(filter?.column_type ?? ''),
    column_sensitive: false,
    column_granularity: '',
    column_remark: filter?.display_group_cn ?? '',
    column_remark_en: filter?.display_group_en ?? '',
    is_dimension: false,
    is_metric: false,
    filter_type: filter?.filter_type ?? '',
    filter_method: getFilterMethodByColumnType(filter?.filter_type ?? ''),
    operation: '',
  };
}

/**
 * 将指标数据转换为 IColumnInfo
 */
export function convertMetricToColumnInfo(metric: IAbConfigMetric, index: number): IColumnInfo {
  return {
    id: `metric_${metric.mapping_id}_${index}`,
    table_id: String(metric.mapping_id),
    table_code: '',
    column_name: metric.display_name_en,
    column_display: metric.display_name_cn,
    column_display_en: metric.display_name_en,
    column_type: convertColumnType(metric.column_type),
    column_sensitive: false,
    column_granularity: '',
    column_remark: metric.display_group_cn,
    column_remark_en: metric.display_group_en,
    is_custom: metric.is_custom,
    is_metric: true,
    is_dimension: false,
    metric_method: metric.is_aggregatable ? 5 : 1,
    is_aggregate: metric.is_aggregatable,
    sql_expression: metric.sql_expression,
    metric_alias: metric.display_name_cn,
  };
}

/**
 * 将维度数据转换为 IColumnInfo
 */
export function convertDimensionToColumnInfo(dimension: IAbConfigDimension, index: number): IColumnInfo {
  return {
    id: `dimension_${dimension.mapping_id}_${index}`,
    table_id: String(dimension.mapping_id),
    table_code: '',
    column_name: dimension.display_name_en,
    column_display: dimension.display_name_cn,
    column_display_en: dimension.display_name_en,
    column_type: convertColumnType(dimension.column_type),
    column_sensitive: false,
    column_granularity: '',
    column_remark: dimension.display_group_cn,
    column_remark_en: dimension.display_group_en,
    is_dimension: true,
    is_metric: false,
    dimension_method: 1,
  };
}

/**
 * 将事件筛选器数据转换为组件需要的格式
 */
export function convertEventFiltersToEventList(
  eventFilters: IAbConfigEventFilter[]
): IEventListItem[] {
  return eventFilters?.map((eventFilter) => {
    const parameters = eventFilter.parameters.map((param) => ({
      param_key: param.param_key,
      param_type: param.param_type,
      param_name_ch: param.param_key_cn,
      param_name_en: param.param_key,
      options: param.options.map((opt) => ({
        code: opt.value,
        name_ch: opt.label_cn,
        name_en: opt.label_en,
        sn: '',
        type: '',
        deviation: '',
      })),
    }));

    return {
      code: eventFilter.event_name,
      name_ch: eventFilter.event_name_cn,
      name_en: eventFilter.event_name,
      sn: '',
      type: eventFilter.source_type,
      deviation: '',
      json_str: JSON.stringify(parameters),
    };
  }) || [];
}


/**
 * 将数据转换为选项格式的公共方法（按分组）
 * @param items - 要转换的数据项数组
 * @param includeTopic - 是否包含 topic 字段（optionFilters 需要）
 * @returns 转换后的选项菜单数组
 */
function convertToOptions<T extends IGroupableItem>(
  items: T[],
): IOptionMenu[] {
  // 按 display_group_cn 分组
  const groupMap = new Map<string, T[]>();

  items.forEach((item) => {
    const groupKey = item.display_group_cn || 'other';
    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, []);
    }
    groupMap.get(groupKey)!.push(item);
  });

  // 收集所有选项（用于"全部"菜单）
  const allOptions = items.map((item) => ({
    value: item.display_name_en,
    name: item.display_name_en,
    name_zh: item.display_name_cn,
    name_en: item.display_name_en,
    desc: item.display_name_cn,
    desc_en: item.display_name_en,
    table_name: '',
  }));

  // 创建"全部"菜单
  const allMenu: IOptionMenu = {
    menu_id: 'all',
    menu_name: '全部',
    menu_name_en: 'All',
    topic: '全部',
    list: [
      {
        tab_id: 'all',
        tab_name: '所有维度',
        options: allOptions,
      },
    ],
  };

  // 转换为选项格式（按分组）
  const groupedOptions = Array.from(groupMap.entries()).map(([groupKey, groupItems]) => {
    const firstItem = groupItems[0];
    const menu: IOptionMenu = {
      menu_id: groupKey.toLowerCase().replace(/\s+/g, '_'),
      menu_name_en: firstItem.display_group_en,
      menu_name: firstItem.display_group_cn,
      topic: firstItem.display_group_cn,
      list: [
        {
          tab_id: 'all',
          tab_name: '所有维度',
          options: groupItems.map((item) => ({
            value: item.display_name_en,
            name: item.display_name_en,
            name_zh: item.display_name_cn,
            name_en: item.display_name_en,
            desc: item.display_name_cn,
            desc_en: item.display_name_en,
            table_name: '',
          })),
        },
      ],
    };
    return menu;
  });

  // 返回"全部"菜单 + 分组菜单
  return [allMenu, ...groupedOptions];
}

/**
 * 将筛选器数据转换为选项格式（按分组）
 */
export function convertFiltersToOptions(filters: IAbConfigFilter[]): IOptionMenu[] {
  return convertToOptions(filters);
}

/**
 * 将指标数据转换为选项格式（按分组）
 */
export function convertMetricsToOptions(metrics: IAbConfigMetric[]): IOptionMenu[] {
  return convertToOptions(metrics);
}

/**
 * 将维度数据转换为选项格式（按分组）
 */
export function convertDimensionsToOptions(dimensions: IAbConfigDimension[]): IOptionMenu[] {
  return convertToOptions(dimensions);
}


/**
 * 将实验筛选配置转为级联组件 data 结构
 */
export function toCascaderData(
  experimentFilters: IAbConfigExperimentFilter[] | undefined
): ICascaderOption[] {
  if (!Array.isArray(experimentFilters) || !experimentFilters.length) return [];
  return experimentFilters.map((layer) => ({
    value: layer.layer_key,
    label: layer.layer_key,
    children: (layer.experiments || []).map((exp) => ({
      value: exp.exp_key,
      label: exp.exp_key,
      children: (exp.groups || []).map((g) => ({
        value: g.exp_group_id,
        label: g.exp_group_name,
      })),
    })),
  }));
}
